import { DISCOUNT_TYPE, PURCHASE_ESIM_STATUS } from "@mozioinc/query";
import { url } from "@mozioinc/utils";
import { useNavigate, useSearch } from "@tanstack/react-location";
import { useMutation, useQuery } from "@tanstack/react-query";
import { posthog } from "posthog-js";
import { useRef, useState } from "react";
import { PriceChangedDialog, PurchaseForm } from "../components";
import { useCurrency } from "../hooks";
import { pushLoggly } from "../utils/logging";
import { esimQuery } from "../utils/query";
import type { PurchaseFormProps } from "../components";
import type { LocationGenerics } from "../pages/routing";

const usePriceChange = () => {
  const [open, setOpen] = useState(false);
  const priceRef = useRef<{
    newPrice?: string;
    checkoutUrl?: string;
    oldPrice?: string;
  }>({});

  return {
    open,
    priceRef,
    handleContinuePurchase: () => {
      window.location.href = priceRef.current.checkoutUrl!;
    },
    handleClose: () => setOpen(false),
    handlePriceChange: (newPrice: string, checkoutUrl: string) => {
      priceRef.current.newPrice = newPrice;
      priceRef.current.checkoutUrl = checkoutUrl;
      setOpen(true);
    },
    saveOldPrice: (oldPrice: string) => {
      priceRef.current.oldPrice = oldPrice;
    },
  };
};

type PurchaseFormContainerProps = Pick<PurchaseFormProps, "countryInputRef">;

const PurchaseFormContainer = (props: PurchaseFormContainerProps) => {
  const { countryInputRef } = props;

  const navigate = useNavigate();

  const countryQuery = useQuery(["countries"], () =>
    esimQuery.getEsimCountries(),
  );

  const { currency: currencyCode } = useCurrency();

  const { countryCode, dataPlan } = useSearch<LocationGenerics>();
  const [coupon, setCoupon] = useState<string>("");

  const planQuery = useQuery(
    ["plans", countryCode, currencyCode],
    () =>
      esimQuery.getEsimPlans({
        countryCode: countryCode || "",
        currency: currencyCode,
      }),
    {
      enabled: Boolean(countryCode),
      refetchOnWindowFocus: true,
    },
  );

  const countries = countryQuery.data ?? [];
  const countryNames = countries.map((c) => c.name) ?? [];
  const plans = planQuery.data ?? [];

  const {
    open,
    priceRef,
    handleContinuePurchase,
    handleClose,
    handlePriceChange,
    saveOldPrice,
  } = usePriceChange();

  const purchaseMutation = useMutation(esimQuery.purchaseEsim, {
    onSuccess: (result) => {
      if (result.status === PURCHASE_ESIM_STATUS.SUCCESS) {
        return navigate({ to: "/purchase-success/" });
      }
      const { newPrice, checkoutUrl } = result;
      if (newPrice !== undefined) {
        const currencySymbol = plans[0]?.price.symbol;
        handlePriceChange(`${currencySymbol}${newPrice}`, checkoutUrl);
        return;
      }
      window.location.href = checkoutUrl;
    },
  });

  const validateCouponMutation = useMutation(esimQuery.validateCoupon);
  const isCouponValidated =
    !validateCouponMutation.isIdle && !validateCouponMutation.isLoading;
  const isCouponValid = validateCouponMutation.isSuccess;

  if (countryQuery.isError) {
    throw countryQuery.error;
  }

  if (planQuery.isError) {
    throw planQuery.error;
  }

  if (purchaseMutation.isError) {
    throw purchaseMutation.error;
  }

  const handleCountryChange = (countryName: string) => {
    const country = countries.find((country) => country.name === countryName);

    navigate({
      search: (old) => ({
        ...old,
        countryCode: country?.code,
        dataPlan: undefined,
      }),
    });
  };

  const handleDataPlanChange = async (value: string) => {
    const plan = plans.find((plan) => plan.value === value);

    if (coupon) {
      validateCouponMutation.reset();
      validateCouponMutation.mutate({
        couponCode: coupon,
        currency: currencyCode,
        esimId: value,
      });
    }

    navigate({
      search: (old) => ({
        ...old,
        dataPlan: plan?.value,
      }),
    });
  };

  const handlePurchase = (planId: string) => {
    pushLoggly({ logType: "purchase", planId: planId });

    const plan = plans.find((plan) => plan.id === planId)!;
    saveOldPrice(`${plan.price.symbol}${plan.price.amount}`);

    purchaseMutation.mutate({
      couponCode: isCouponValidated && isCouponValid ? coupon : undefined,
      purchasePayload: plan.metadata.purchasePayload,
      purchaseFailUrl: url.setPathname("/esim/purchase-fail/"),
      purchaseSuccessUrl: url.setPathname("/esim/purchase-success/"),
    });

    posthog.capture("eSIM: Purchase Form Submitted", {
      esim_id: planId,
      country_code: countryCode,
      country_name: countries.find((country) => country.code === countryCode)
        ?.name,
      mobile_data: plan.dataGb,
      validity_day_count: plan.validityDays,
      price_currency: plan.price.currency,
      price_amount: plan.price.amount,
    });
  };

  const handleDialogClose = () => {
    handleClose();
    planQuery.refetch();
  };

  const handleCouponValidation = (coupon: string) => {
    if (!coupon || coupon.trim() === "") {
      validateCouponMutation.reset();
      return;
    }

    setCoupon(coupon);

    if (!dataPlan) {
      return;
    }

    validateCouponMutation.mutate({
      couponCode: coupon,
      currency: currencyCode,
      esimId: dataPlan ?? "",
    });
  };

  const getDiscount = () => {
    if (validateCouponMutation.data?.discount.type === DISCOUNT_TYPE.FIXED) {
      return "";
    }

    return `-${validateCouponMutation.data?.discount.value ?? 0}%`;
  };

  const getFinalPrice = () => {
    if (isCouponValidated && isCouponValid) {
      return `${validateCouponMutation.data?.discountedPrice.symbol}
              ${validateCouponMutation.data?.discountedPrice.amount}`;
    }
    return undefined;
  };

  return (
    <>
      <PurchaseForm
        countryInputRef={countryInputRef}
        countryFieldProps={{
          loading: countryQuery.isFetching,
          countries: countryNames,
          onChange: handleCountryChange,
          value:
            countries.find((country) => country.code === countryCode)?.name ||
            "",
        }}
        planFieldProps={{
          loading: planQuery.isFetching,
          plans: plans.map((plan) => ({
            ...plan,
            price: `${plan.price.symbol}${plan.price.amount}`,
          })),
          value: plans.find((plan) => plan.value === dataPlan)?.value || "",
          onChange: handleDataPlanChange,
        }}
        purchaseLoading={
          purchaseMutation.isLoading || purchaseMutation.isSuccess
        }
        onSubmit={(data) => handlePurchase(data.plan)}
        couponFieldProps={{
          discount: getDiscount(),
          isValidated: isCouponValidated,
          isValid: isCouponValidated && isCouponValid,
          finalPrice: getFinalPrice(),
          onValidation: handleCouponValidation,
        }}
      />
      <PriceChangedDialog
        oldPrice={priceRef.current.oldPrice!}
        newPrice={priceRef.current.newPrice!}
        open={open}
        onClose={handleDialogClose}
        onContinue={handleContinuePurchase}
      />
    </>
  );
};

export { PurchaseFormContainer as PurchaseForm };
